import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import { LinkTo } from '@ember/routing';
import Chip from 'tio-reimagined/components/chip';
import { Text } from 'tio-ui/components/typography';
import { PhBank, PhStar, PhArrowRight, PhTrendDown, PhCalendar } from 'ember-phosphor-icons';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';

/**
 * LoanProgressCard - Visual loan account card with progress visualization
 *
 * Shows loan balance, progress toward payoff, and key metrics in a
 * visually engaging card format consistent with Career Pathways design.
 *
 * @example
 * ```gts
 * <LoanProgressCard @loan={{this.mohela}} @onSelect={{this.selectLoan}} />
 * ```
 */
interface LoanProgressCardSignature {
  Args: {
    /** The loan account to display */
    loan: LoanAccount;
    /** Whether this is the primary/featured loan */
    featured?: boolean;
  };
  Element: HTMLDivElement;
}

export default class LoanProgressCard extends Component<LoanProgressCardSignature> {
  get loan() {
    return this.args.loan;
  }

  get progressPercent() {
    const paid = this.loan.originalBalance - this.loan.balance;
    return Math.round((paid / this.loan.originalBalance) * 100);
  }

  get progressStyle() {
    return htmlSafe(`width: ${this.progressPercent}%`);
  }

  get ringProgress() {
    // SVG circle dasharray calculation for progress ring
    const circumference = 2 * Math.PI * 40; // radius = 40
    const offset = circumference - (this.progressPercent / 100) * circumference;
    return htmlSafe(`stroke-dasharray: ${circumference}; stroke-dashoffset: ${offset}`);
  }

  get formattedBalance() {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(this.loan.balance);
  }

  get formattedMonthly() {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(this.loan.monthlyPayment);
  }

  get formattedPayoff() {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric',
    }).format(new Date(this.loan.estimatedPayoff));
  }

  get accountTypeLabel() {
    const labels = {
      federal: 'Federal',
      private: 'Private',
      refinanced: 'Refinanced',
    };
    return labels[this.loan.accountType];
  }

  get accountTypeIntent() {
    const intents = {
      federal: 'primary' as const,
      private: 'warning' as const,
      refinanced: 'success' as const,
    };
    return intents[this.loan.accountType];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="bg-white rounded-xl p-5 shadow-sm border-2 transition-all hover:shadow-md
        {{if @featured 'border-emerald-300 ring-2 ring-emerald-100' 'border-gray-200'}}"
      ...attributes
    >
      {{! Header with servicer and status }}
      <div class="flex items-start justify-between mb-4">
        <div class="flex items-center gap-3">
          <div
            class="p-2.5 rounded-lg
              {{if @featured 'bg-emerald-100 text-emerald-600' 'bg-gray-100 text-gray-600'}}"
          >
            <PhBank @size={{24}} />
          </div>
          <div>
            <div class="font-semibold text-gray-900">{{this.loan.servicer}}</div>
            <Text @intent="muted" class="text-sm">
              {{this.loan.loanCount}}
              {{if (eq this.loan.loanCount 1) "loan" "loans"}}
            </Text>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Chip @intent={{this.accountTypeIntent}}>{{this.accountTypeLabel}}</Chip>
          {{#if this.loan.isReceivingEmployerHelp}}
            <div
              class="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium"
              title="Receiving employer contributions"
            >
              <PhStar @size={{14}} weight="fill" />
              <span>Employer Help</span>
            </div>
          {{/if}}
        </div>
      </div>

      {{! Progress Ring and Balance }}
      <div class="flex items-center gap-6 mb-4">
        {{! Progress Ring }}
        <div class="relative w-24 h-24 flex-shrink-0">
          <svg class="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            {{! Background circle }}
            <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" stroke-width="8" />
            {{! Progress circle }}
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke={{if @featured "#10b981" "#6366f1"}}
              stroke-width="8"
              stroke-linecap="round"
              style={{this.ringProgress}}
            />
          </svg>
          <div class="absolute inset-0 flex flex-col items-center justify-center">
            <span class="text-lg font-bold text-gray-900">{{this.progressPercent}}%</span>
            <span class="text-xs text-gray-500">paid</span>
          </div>
        </div>

        {{! Balance and Details }}
        <div class="flex-1">
          <div class="text-3xl font-bold text-gray-900 mb-1">{{this.formattedBalance}}</div>
          <div class="flex items-center gap-1 text-emerald-600 text-sm mb-2">
            <PhTrendDown @size={{16}} />
            <span>{{this.formattedMonthly}}/mo payment</span>
          </div>
          <div class="text-sm text-gray-500">
            <span class="font-medium">{{this.loan.interestRate}}%</span>
            interest rate
          </div>
        </div>
      </div>

      {{! Footer with payoff info }}
      <div class="flex items-center justify-between pt-4 border-t border-gray-100">
        <div class="flex items-center gap-2 text-gray-600">
          <PhCalendar @size={{18}} />
          <span class="text-sm">
            <span class="font-medium">Payoff:</span>
            {{this.formattedPayoff}}
          </span>
        </div>
        <LinkTo
          @route="authenticated.reimagined.loans.detail"
          @model={{this.loan.id}}
          class="flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
        >
          <span>View Details</span>
          <PhArrowRight @size={{16}} />
        </LinkTo>
      </div>
    </div>
  </template>
}

const eq = (a: unknown, b: unknown) => a === b;
